import { Phone, PhoneCallbackOutlined, PhoneDisabled } from '@mui/icons-material';
import PhoneForwardedOutlined from '@mui/icons-material/PhoneForwardedOutlined';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import { Call } from '@twilio/voice-sdk';
import { useNotify } from 'react-admin';
import { usePhoneCalls } from '../../../../contexts/PhoneCallsContext/PhoneCallsContext';

interface IncomingCallProps {
  open: boolean;
  type: 'member' | 'transfer' | 'agent';
  purpose?: string;
  incomingCall: Call | null;
}

export const IncomingCallPopup = ({ open, type, purpose, incomingCall }: IncomingCallProps) => {
  const notify = useNotify();
  const { rejectIncomingCall, answerIncomingCall } = usePhoneCalls();

  if (!incomingCall) {
    return null;
  }

  const handleAnswerCall = async () => {
    if (!incomingCall) {
      return;
    }

    try {
      answerIncomingCall(incomingCall);
    } catch (error) {
      console.error('Error answering call:', error);
      notify('Failed to answer call', 'error');
    }
  };

  const handleRejectCall = async () => {
    try {
      rejectIncomingCall(incomingCall);
    } catch (error) {
      console.error('Error rejecting call:', error);
    }
  };

  const handleClose = (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (reason && reason === 'backdropClick') {
      return;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='xs'
      fullWidth
      sx={{
        '& .MuiDialog-container > .MuiPaper-elevation': {
          borderRadius: '13px',
          padding: '10px',
        },
      }}>
      <DialogTitle
        sx={{
          backgroundColor: '#fff',
          textAlign: 'center',
          fontWeight: 'bold',
          fontSize: '1.25rem',
        }}>
        {type === 'member' ? (
          <Box display='flex' gap={1.5} alignItems='center' justifyContent='center'>
            <PhoneCallbackOutlined />
            <Typography fontSize='18px' fontWeight={400}>
              <strong>Incoming Call</strong>
            </Typography>
          </Box>
        ) : (
          <Box display='flex' gap={1} alignItems='center' justifyContent='center'>
            <PhoneForwardedOutlined />
            <Typography variant='h6'>Transferred Call</Typography>
          </Box>
        )}
      </DialogTitle>

      <DialogContent sx={{ textAlign: 'center', p: 3 }}>
        {type === 'member' ? (
          <Box sx={{ mb: 3 }}>
            <Typography variant='subtitle1' sx={{ mb: 1 }}>
              Incoming Call From:
            </Typography>
            <Typography variant='body1' sx={{ fontWeight: 500 }}>
              {incomingCall.parameters?.From || 'Unknown Number'}
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant='body1' sx={{ mb: 1 }}>
              <strong>Member Name:</strong> {incomingCall.outboundConnectionId || 'Unknown'}
            </Typography>
            <Typography variant='body1' sx={{ mb: 1 }}>
              <strong>From:</strong> {incomingCall.callerInfo || 'Agent Name'}
            </Typography>
            <Typography variant='body1' sx={{ mb: 2 }}>
              <strong>Call purpose:</strong> {purpose || 'Unknown'}
            </Typography>
          </>
        )}

        {/* Action Buttons */}

        {/* Reject Call */}
        <Box display='flex' justifyContent='center' gap={4} mt={2}>
          <IconButton
            onClick={handleRejectCall}
            sx={{
              backgroundColor: '#E53935',
              color: 'white',
              width: 45,
              height: 45,
              '&:hover': { backgroundColor: '#c62828' },
            }}>
            <PhoneDisabled fontSize='medium' />
          </IconButton>

          {/* Accept Call */}
          <IconButton
            onClick={handleAnswerCall}
            sx={{
              backgroundColor: '#43A047',
              color: 'white',
              width: 45,
              height: 45,
              '&:hover': { backgroundColor: '#2e7d32' },
            }}>
            <Phone fontSize='medium' />
          </IconButton>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
